import React, { useEffect, useMemo, useState } from "react";
import { toUTCISOString } from "../../utils/timeZone";
import _ from "lodash";
import { useForm } from "react-hook-form";
import MainLayout from "../../examples/LayoutContainers/MainLayout";
import MainNavbar from "../../examples/Navbars/MainNavbar";
import DataTable from "../../examples/Tables/DataTable";
import { tablePanelStyles } from "../../examples/Tables/styles";
import FormProvider from "../../components/FormProviders";
import SoftBox from "../../components/SoftBox";
import SoftTypography from "../../components/SoftTypography";
import { getToken } from "../../utils/cache";
import { get, getMerchant, set } from "../../utils/localStorage";
import CreatedTime from "../../components/CreatedTime";
import { hasPermission } from "../../utils/roles";
import { Permissions } from "../../constants/roles";
import SettlementsFilters from "./components/SettlementsFilters";
import { useDownloadSettlement, useGetSettlements } from "./hooks";

const LC_PAYLOAD = "merchantSettlementsPayload";
const DEFAULT_LIMIT = 50;
const DEFAULT_SORT_BY = "createdAt";
const DEFAULT_SORT_DIRECTION = "desc";
const SORT_COLUMNS = [
  "settlementId",
  "_id",
  "key",
  "startDate",
  "endDate",
  "txCurrency",
  "settlementCurrency",
  "createdAt",
];

const sanitizeRequest = ({ fromDate, toDate, page, limit, sortBy, sortDirection }) => {
  return _.pickBy({ fromDate, toDate, page, limit, sortBy, sortDirection }, _.identity);
};

const formatDate = (value) => {
  if (!value) {
    return undefined;
  }

  return toUTCISOString(value);
};

const formatAmount = (amount, currency) => {
  if (amount === null || amount === undefined) {
    return "-";
  }

  const formattedAmount = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(amount));

  return currency ? `${formattedAmount} ${currency}` : formattedAmount;
};

const Settlements = () => {
  const token = getToken();
  const merchant = getMerchant();
  const storedPayload = get(LC_PAYLOAD) || {};
  const [pageSize, setPageSize] = useState(Number(storedPayload.limit) || DEFAULT_LIMIT);
  const [currentPage, setCurrentPage] = useState(Number(storedPayload.page) || 1);
  const [sortBy, setSortBy] = useState(storedPayload.sortBy || DEFAULT_SORT_BY);
  const [sortDirection, setSortDirection] = useState(
    storedPayload.sortDirection || DEFAULT_SORT_DIRECTION
  );
  const [downloadingSettlementId, setDownloadingSettlementId] = useState(null);
  const canDownload = hasPermission(Permissions.SETTLEMENT.DOWNLOAD);

  const methods = useForm({
    defaultValues: {
      fromDate: storedPayload.fromDate || null,
      toDate: storedPayload.toDate || null,
    },
  });
  const { handleSubmit, reset, getValues } = methods;
  const { mutate, isLoading, data } = useGetSettlements();
  const { mutate: downloadSettlement } = useDownloadSettlement();

  const buildPayload = (values = {}, overrides = {}) => {
    return sanitizeRequest({
      fromDate: formatDate(values.fromDate),
      toDate: formatDate(values.toDate),
      page: overrides.page ?? currentPage,
      limit: overrides.limit ?? pageSize,
      sortBy: overrides.sortBy ?? sortBy,
      sortDirection: overrides.sortDirection ?? sortDirection,
    });
  };

  const executeSearch = (values, overrides = {}) => {
    const payload = buildPayload(values, overrides);
    set(LC_PAYLOAD, payload);
    mutate(payload);
  };

  const onSubmit = (values) => {
    setCurrentPage(1);
    executeSearch(values, { page: 1 });
  };

  const onClearFilters = () => {
    const emptyValues = {
      fromDate: null,
      toDate: null,
    };

    reset(emptyValues);
    setCurrentPage(1);
    executeSearch(emptyValues, { page: 1 });
  };

  const handlePageChange = (page) => {
    const nextPage = page + 1;
    setCurrentPage(nextPage);
    executeSearch(getValues(), { page: nextPage });
  };

  const handlePageSizeChange = (nextPageSize) => {
    const parsedPageSize = Number(nextPageSize);

    if (parsedPageSize === pageSize) {
      return;
    }

    setCurrentPage(1);
    setPageSize(parsedPageSize);
    executeSearch(getValues(), { page: 1, limit: parsedPageSize });
  };

  const handleSortChange = (column) => {
    if (!SORT_COLUMNS.includes(column)) {
      return;
    }

    const nextSortDirection =
      sortBy === column
        ? sortDirection === "asc"
          ? "desc"
          : "asc"
        : column === "createdAt"
          ? "desc"
          : "asc";

    setCurrentPage(1);
    setSortBy(column);
    setSortDirection(nextSortDirection);
    executeSearch(getValues(), { page: 1, sortBy: column, sortDirection: nextSortDirection });
  };

  const handleDownload = (id) => {
    if (!canDownload) {
      return;
    }

    setDownloadingSettlementId(id);
    downloadSettlement(id, {
      onSettled: () => {
        setDownloadingSettlementId(null);
      },
      onSuccess: (response) => {
        if (response?.url) {
          window.open(response.url, "_blank");
        }
      },
    });
  };

  const rows = useMemo(() => {
    return _.map(data?.docs || [], (item) => ({
      settlementId: item._id,
      fileName:
        downloadingSettlementId === item._id ? (
          <SoftTypography variant="caption" color="text">
            Downloading...
          </SoftTypography>
        ) : canDownload && item.key ? (
          <SoftTypography
            variant="button"
            color="info"
            fontWeight="medium"
            title={item.key}
            sx={{
              cursor: "pointer",
              textDecoration: "underline",
              wordBreak: "break-all",
            }}
            onClick={() => handleDownload(item._id)}
          >
            {item.key}
          </SoftTypography>
        ) : (
          item.key || "-"
        ),
      startDate: item.startDate || "-",
      endDate: item.endDate || "-",
      txCurrency: item.txCurrency || "-",
      settlementCurrency: item.settlementCurrency || "-",
      amountToPay: formatAmount(item.summary?.amountToPay, item.settlementCurrency),
      createdAt: item.createdAt || "-",
    }));
  }, [canDownload, data?.docs, downloadingSettlementId]);

  useEffect(() => {
    if (!token) {
      window.location.href = "/sign-in";
    }
  }, [token]);

  useEffect(() => {
    if (!merchant?._id) {
      return;
    }

    executeSearch(
      {
        fromDate: storedPayload.fromDate || null,
        toDate: storedPayload.toDate || null,
      },
      {
        page: Number(storedPayload.page) || 1,
        limit: Number(storedPayload.limit) || pageSize,
        sortBy: storedPayload.sortBy || sortBy,
        sortDirection: storedPayload.sortDirection || sortDirection,
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merchant?._id]);

  return (
    <MainLayout>
      <MainNavbar />
      <SoftBox sx={tablePanelStyles}>
        <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
          <SettlementsFilters onClearFilters={onClearFilters} />
        </FormProvider>
        <DataTable
          embedded
          table={{
            columns: [
              { Header: "Settlement ID", accessor: "settlementId" },
              { Header: "File Name", accessor: "fileName" },
              { Header: "Start Date", accessor: "startDate", Cell: CreatedTime },
              { Header: "End Date", accessor: "endDate", Cell: CreatedTime },
              { Header: "Transaction Currency", accessor: "txCurrency" },
              { Header: "Settlement Currency", accessor: "settlementCurrency" },
              { Header: "Amount To Pay", accessor: "amountToPay" },
              { Header: "Created At", accessor: "createdAt", Cell: CreatedTime },
            ],
            rows,
          }}
          entriesPerPage={{
            defaultValue: pageSize,
            entries: [25, 50, 100, 200],
          }}
          isLoading={isLoading}
          manualPagination={true}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          pageCount={_.get(data, "totalPages", 0)}
          rowCount={_.get(data, "totalDocs", 0)}
          currentPage={currentPage}
          onHeaderCellClick={handleSortChange}
          sortBy={sortBy}
          sortDirection={sortDirection}
          sortColumns={SORT_COLUMNS}
        />
      </SoftBox>
    </MainLayout>
  );
};

export default Settlements;
